import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Breadcrumbs, Button, Card, Chip, EmptyState } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang, type Lang } from '../../i18n';
import { get } from '../../services/api';
import './ServicesPage.scss';

interface Category { slug: string; title: string }
interface Service {
  slug: string;
  title: string;
  short_description?: string;
  description?: string;
  category_slug?: string;
  category?: string;
  bullets?: string[];
  tags?: string[];
}
interface ListResponse<T> { items?: T[]; results?: T[] }

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function ServicesPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [activeCat, setActiveCat] = useState<string | null>(params.get('category'));
  const [activeTag, setActiveTag] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const ls = LANG_MAP[lang];
        const cats = await get<ListResponse<Category>>(`/services/categories/?lang=${ls}`);
        const svcs = await get<ListResponse<Service>>(`/services/?lang=${ls}&page_size=100`);
        if (cancel) return;
        setCategories(cats?.results || cats?.items || []);
        setServices(svcs?.results || svcs?.items || []);
      } catch {
        if (!cancel) { setCategories([]); setServices([]); }
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  useEffect(() => {
    const q = params.get('category');
    if (q !== activeCat) setActiveCat(q);
  }, [params]);

  const setCategoryFilter = (slug: string | null) => {
    setActiveCat(slug);
    const next = new URLSearchParams(params);
    if (slug) next.set('category', slug); else next.delete('category');
    setParams(next, { replace: true });
  };

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const s of services) for (const tg of s.tags || []) set.add(tg);
    return Array.from(set);
  }, [services]);

  const filtered = useMemo(() => services.filter((s) => {
    const cat = s.category_slug || s.category;
    if (activeCat && cat !== activeCat) return false;
    if (activeTag && !(s.tags || []).includes(activeTag)) return false;
    return true;
  }), [services, activeCat, activeTag]);

  useDocumentHead({
    title: `${t('services.title')} — ${t('brand.name')}`,
    description: t('services.sub'),
    canonical: 'https://ukam.uz/services',
    og: { title: t('services.title'), description: t('services.sub'), type: 'website' },
    schemaOrg: [
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
        { '@type': 'ListItem', position: 2, name: t('services.title'), item: 'https://ukam.uz/services' },
      ] },
    ],
  });

  return (
    <div className="services-page">
      <div className="container services-page__head">
        <Breadcrumbs items={[{ label: t('brand.name'), href: '/' }, { label: t('services.title') }]} />
        <h1 className="services-page__title">{t('services.title')}</h1>
        <p className="services-page__sub">{t('services.sub')}</p>
      </div>
      <div className="container services-page__body">
        <aside className="services-page__filters">
          <h3 className="services-page__filter-title">{t('services.filters')}</h3>
          <div className="services-page__cat-buttons">
            <button
              type="button"
              className={`services-page__cat-btn${!activeCat ? ' services-page__cat-btn--active' : ''}`}
              onClick={() => setCategoryFilter(null)}
            >
              {t('services.all')}
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                className={`services-page__cat-btn${activeCat === c.slug ? ' services-page__cat-btn--active' : ''}`}
                onClick={() => setCategoryFilter(c.slug)}
              >
                {c.title}
              </button>
            ))}
          </div>
          {allTags.length > 0 && (
            <>
              <h3 className="services-page__filter-title">{t('services.tags')}</h3>
              <div className="services-page__chips">
                {allTags.map((tg) => (
                  <Chip key={tg} variant={activeTag === tg ? 'brand' : 'neutral'} onClick={() => setActiveTag(activeTag === tg ? null : tg)}>{tg}</Chip>
                ))}
              </div>
            </>
          )}
        </aside>
        <div className="services-page__list">
          {filtered.length === 0 ? (
            <EmptyState title={t('services.empty')} />
          ) : (
            <div className="grid grid--4 services-page__grid">
              {filtered.map((s) => {
                const cat = s.category_slug || s.category || '';
                const desc = s.short_description || s.description;
                const href = `/services/${cat}/${s.slug}`;
                return (
                  <Card
                    key={s.slug}
                    variant="elevated"
                    padding="md"
                    className="services-page__card services-page__card--clickable"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(href)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(href);
                      }
                    }}
                  >
                    <h3 className="services-page__card-title">{s.title}</h3>
                    {desc && <p className="services-page__card-desc">{desc}</p>}
                    {s.bullets && s.bullets.length > 0 && (
                      <ul className="services-page__bullets">
                        {s.bullets.slice(0, 3).map((b, i) => (
                          <li key={i}><Check size={14} /><span>{b}</span></li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to={href}
                      className="services-page__more"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="sm" iconRight={<ArrowRight size={14} />}>{t('services.details')}</Button>
                    </Link>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
